import React from 'react';
import { Box, Typography } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';

/**
 * SavedMapItemList — vertical, scrollable list of horizontal rectangle cards
 * shown next to the map on the แผนที่ (map) tab in the desktop saved panel.
 * Each card mirrors one marker on the map; clicking it recenters the map.
 * Reads place-shaped fields (with saved-item fallbacks) so it works for both
 * the user's own saved items and a selected reviewer's items.
 */

const getName = (it) =>
  it.event_name || it.place_name || it.name || it.item_name || it.venue_name || '';

// Match the app's image resolution (see PlaceDetails) so thumbnails don't fail.
const getImage = (it) =>
  it.generated_image_url ||
  it.image ||
  it.image_url ||
  it.cover_image_url ||
  it.poster_url ||
  it.event_image_link ||
  it.main_image_url ||
  it.item_image_url ||
  (it.photo && it.photo.images && it.photo.images.large && it.photo.images.large.url) ||
  it.thumbnail_url ||
  null;

const getLoc = (it) =>
  it.event_location || it.item_location || it.location || it.address || '';

const getKey = (it, i) =>
  it.id || it.event_id || (it.item_type && it.item_id ? `${it.item_type}_${it.item_id}` : i);

const SavedMapItemList = ({ items = [], onItemClick }) => (
  <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
    {items.map((it, i) => {
      const name = getName(it);
      const img = getImage(it);
      const loc = getLoc(it);
      return (
        <Box
          key={getKey(it, i)}
          onClick={() => onItemClick && onItemClick(it)}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 1.25,
            p: 1,
            borderRadius: 2,
            backgroundColor: '#fff',
            border: '1px solid #eef0f4',
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            cursor: onItemClick ? 'pointer' : 'default',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
            '&:hover': onItemClick ? {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderColor: '#e2e8f0',
            } : {},
          }}
        >
          {img ? (
            <Box
              component="img"
              src={img}
              alt={name}
              loading="lazy"
              sx={{ width: 120, height: 84, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0, backgroundColor: '#f3f4f6' }}
            />
          ) : (
            <Box sx={{ width: 120, height: 84, borderRadius: 1.5, backgroundColor: '#f3f4f6', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
              <PlaceOutlinedIcon sx={{ color: '#9ca3af' }} />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography sx={{
              fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {name || '—'}
            </Typography>
            {loc && (
              <Typography sx={{
                mt: 0.5, fontSize: '0.75rem', color: 'text.secondary',
                display: 'flex', alignItems: 'center', gap: 0.5,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                <PlaceOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                {loc}
              </Typography>
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);

export default SavedMapItemList;
